import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import { useQuery } from 'src/hooks/useQuery';
import { makeRouteScenario } from 'src/utils/route';
import { ErrorLayout } from 'app/ErrorLayout';
import { isOrderScenario, OrderScenario } from 'components/order/model/Order';
import { getLocalMerchantId } from 'components/settings/localStore';
import { Throbber } from 'lib/Throbber';
import { useLocalHistory } from 'lib/useLocalHistory';
import { resetReturnUrl } from 'store/auth/authSlice';
import { resetBasket, setBasket, setDeliveryLocation } from 'store/basket/basketSlice';
import { ROUTES } from './routes';

export const OrderEntryPage: React.FC = () => {
    const tableNumber = useQuery('tableNumber');
    const scenario = useQuery('scenario');
    const locationId = useQuery('locationId');
    const { t } = useTranslation();
    const { push, getParsedPath } = useLocalHistory();
    const dispatch = useAppDispatch();
    const parsedScenario = React.useMemo(() => (!!scenario ? scenario : OrderScenario.PREORDER), [scenario]);
    const hasInvalidParam = React.useMemo(
        () => !locationId && parsedScenario === String(OrderScenario.ORDER_TO_TABLE),
        [locationId, parsedScenario]
    );
    const description = hasInvalidParam ? t('ORDER_ENTRY_INVALID_LINK_PARAM') : t('ORDER_ENTRY_INVALID_LINK');
    const validationError = React.useMemo(() => {
        if (!isOrderScenario(scenario)) {
            return `Error: ${scenario} is an invalid Scenario`;
        }
        if (hasInvalidParam) {
            return 'Invalid Link: Missing Location';
        }
        return '';
    }, [hasInvalidParam, scenario]);

    // This effect should take all the information from the URL, and set it in the correct places
    // after which it should redirect the user to the correct page, for it to handle the scenario further
    React.useEffect(() => {
        if (scenario && !!validationError) {
            return; // Something isn't valid. Ignore this request
        }

        // Reset previous states that might have been loaded from LocalStorage
        dispatch(resetReturnUrl());
        dispatch(resetBasket());
        let searchState = undefined;

        if (!!tableNumber) {
            dispatch(setDeliveryLocation(tableNumber));
            searchState = `tableNumber=${tableNumber}`;
        }

        // If we have a location, redirect to the menu page
        if (!!locationId) {
            const merchantId = getLocalMerchantId();
            if (scenario) {
                dispatch(setBasket({ locationId }));
                if (merchantId) {
                    if (isOrderScenario(scenario) && scenario === OrderScenario.TABLE) {
                        push(
                            ROUTES.QUICKPAY.CHECKOUT,
                            {
                                merchantId,
                                locationId,
                                scenario: makeRouteScenario(scenario)
                            },
                            searchState
                        );
                        return;
                    }
                }
            } else {
                dispatch(setBasket({ locationId }));
                if (merchantId) {
                    push(ROUTES.JOURNEY.LANDING, { merchantId, locationId });
                    return;
                }
            }
            push(ROUTES.MENU, { locationId, scenario: makeRouteScenario(scenario as OrderScenario) });
            return;
        }

        // Redirect to Location List page
        push(ROUTES.BASE, { scenario });
    }, [validationError, locationId, scenario, tableNumber, dispatch, push]);

    const fallbackUrl = React.useMemo(() => getParsedPath(ROUTES.BASE), [getParsedPath]);

    return (
        <>
            {!!validationError && (
                <ErrorLayout
                    title={t('GENERAL_GENERIC_ERROR')}
                    description={description}
                    fallbackTitle="Home"
                    fallbackUrl={fallbackUrl}
                    errorMessage={validationError}
                />
            )}
            {!validationError && <Throbber text={t('ORDER_ENTRY_LOADING')} />}
        </>
    );
};
